import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, Users, Calendar, FileText, Clock,
  ChevronRight, Search, Eye, Edit, Trash2, Send,
  Download, Phone, Mail, MapPin, MessageCircle, Plus,
  CheckCircle, XCircle, AlertCircle, User, Euro, Calculator,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_CONFIG = {
  nouveau: { label: 'Nouvelle demande', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  en_cours: { label: 'En cours de traitement', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  devis_envoye: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-800', icon: FileText },
  accepte: { label: 'Accepté', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: XCircle },
  planifie: { label: 'Mission planifiée', color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
  termine: { label: 'Terminé', color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
};

const AdminLivraisonB2C = ({ user, activeModule = "b2c", view = "dashboard" }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(view === 'dashboard' ? 'demandes' : view);
  const [demandes, setDemandes] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDemandeModal, setShowDemandeModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  
  // Quote form
  const [quoteData, setQuoteData] = useState({
    transport_price: '',
    labor_price: '',
    extra_fees: '',
    extra_description: '',
    notes: ''
  });

  // Planning form
  const [planningData, setPlanningData] = useState({
    date: '',
    time: '',
    assigned_driver: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [demandesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/demandes-transport-b2c`, { withCredentials: true }),
        axios.get(`${API}/users?status=active`, { withCredentials: true })
      ]);
      setDemandes(demandesRes.data);
      setEmployees(employeesRes.data.filter(e => e.role === 'employee'));
      
      // Build planning from accepted/planned demandes
      const plannedDemandes = demandesRes.data.filter(d => 
        d.status === 'planifie' || d.status === 'accepte'
      );
      setPlanning(plannedDemandes);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateDemandeStatus = async (demandeId, newStatus) => {
    try {
      await axios.put(`${API}/demandes-transport-b2c/${demandeId}/status?status=${newStatus}`, {}, { withCredentials: true });
      setDemandes(prev => prev.map(d => 
        d.demande_id === demandeId ? { ...d, status: newStatus } : d
      ));
      if (selectedDemande?.demande_id === demandeId) {
        setSelectedDemande(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const deleteDemande = async (demandeId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API}/demandes-transport-b2c/${demandeId}`, { withCredentials: true });
      setDemandes(prev => prev.filter(d => d.demande_id !== demandeId));
      setShowDemandeModal(false);
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const calculateTotal = () => {
    const transport = parseFloat(quoteData.transport_price) || 0;
    const labor = parseFloat(quoteData.labor_price) || 0;
    const extra = parseFloat(quoteData.extra_fees) || 0;
    return (transport + labor + extra).toFixed(2);
  };

  const generateQuotePDF = () => {
    if (!selectedDemande) return;
    
    const total = calculateTotal();
    const content = `
DEVIS DE TRANSPORT
==================

DLV EXPRESS
41 rue d'Aulnay
95500 Gonesse
WhatsApp: 06 41 88 89 52
Email: transport@dlv-express.com

Date: ${new Date().toLocaleDateString('fr-FR')}
Référence: ${selectedDemande.demande_id}

CLIENT
------
Nom: ${selectedDemande.name}
Téléphone: ${selectedDemande.phone}
Email: ${selectedDemande.email}

DÉTAILS DU TRANSPORT
--------------------
Départ: ${selectedDemande.address_from}
Arrivée: ${selectedDemande.address_to}
Type de marchandise: ${selectedDemande.merchandise_type || 'Non précisé'}
Volume: ${selectedDemande.volume || 'Non précisé'}
Date souhaitée: ${selectedDemande.date || 'À définir'}

DÉTAIL DU DEVIS
---------------
Transport: ${quoteData.transport_price || '0'} €
Main d'œuvre: ${quoteData.labor_price || '0'} €
Frais supplémentaires: ${quoteData.extra_fees || '0'} €
${quoteData.extra_description ? `(${quoteData.extra_description})` : ''}

TOTAL: ${total} € TTC

${quoteData.notes ? `Notes: ${quoteData.notes}` : ''}

---
Ce devis est valable 30 jours.
    `;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devis_transport_${selectedDemande.demande_id}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const sendQuoteByEmail = async () => {
    if (!selectedDemande) return;
    
    const total = calculateTotal();
    try {
      await axios.post(`${API}/demandes-transport-b2c/${selectedDemande.demande_id}/send-quote`, {
        transport_price: quoteData.transport_price,
        labor_price: quoteData.labor_price,
        extra_fees: quoteData.extra_fees,
        extra_description: quoteData.extra_description,
        total: total,
        notes: quoteData.notes
      }, { withCredentials: true });
      
      alert('Devis envoyé par email avec succès !');
      await updateDemandeStatus(selectedDemande.demande_id, 'devis_envoye');
      setShowQuoteModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to send quote:', error);
      alert('Erreur lors de l\'envoi du devis');
    }
  };

  const savePlanning = async () => {
    if (!selectedDemande || !planningData.date) return;
    
    try {
      await axios.put(`${API}/demandes-transport-b2c/${selectedDemande.demande_id}/planning`, {
        planned_date: planningData.date,
        planned_time: planningData.time,
        assigned_driver: planningData.assigned_driver,
        notes: planningData.notes
      }, { withCredentials: true });
      
      await updateDemandeStatus(selectedDemande.demande_id, 'planifie');
      setShowPlanningModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save planning:', error);
      alert('Erreur lors de la planification');
    }
  };

  // Filter demandes
  const filteredDemandes = demandes.filter(d => {
    const matchesSearch = 
      d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.address_from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.address_to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.merchandise_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: demandes.length,
    nouveau: demandes.filter(d => d.status === 'nouveau').length,
    en_cours: demandes.filter(d => d.status === 'en_cours' || d.status === 'devis_envoye').length,
    planifie: demandes.filter(d => d.status === 'planifie' || d.status === 'accepte').length
  };

  if (loading) {
    return (
      <Layout user={user} showBackButton onBack={() => navigate('/admin')}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6">
        {/* Header - Adapté selon la vue */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-7 h-7 text-blue-500" />
              {view === 'dashboard' && 'Livraison B2C - Tableau de bord'}
              {view === 'demandes' && 'Demandes clients'}
              {view === 'devis' && 'Devis B2C'}
              {view === 'missions' && 'Missions / Interventions'}
              {view === 'planning' && 'Planning B2C'}
              {view === 'clients' && 'Clients B2C'}
              {view === 'facturation' && 'Facturation'}
            </h1>
            <p className="text-slate-500 mt-1">Transport pour particuliers</p>
          </div>
        </div>

        {/* Vue Dashboard */}
        {view === 'dashboard' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total demandes</p>
                      <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                    </div>
                    <FileText className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600 font-medium">Nouvelles</p>
                      <p className="text-2xl font-bold text-yellow-900">{stats.nouveau}</p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">En cours</p>
                      <p className="text-2xl font-bold text-purple-900">{stats.en_cours}</p>
                    </div>
                    <Clock className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Planifiés</p>
                      <p className="text-2xl font-bold text-green-900">{stats.planifie}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Quick access cards for dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/b2c/demandes')}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Demandes clients</h3>
                    <p className="text-sm text-slate-500">{stats.nouveau} nouvelles demandes</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/b2c/devis')}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Euro className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Devis</h3>
                    <p className="text-sm text-slate-500">Gérer les devis clients</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/b2c/planning')}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Planning</h3>
                    <p className="text-sm text-slate-500">{stats.planifie} missions planifiées</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Vue Demandes */}
        {view === 'demandes' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, email, adresse, marchandise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg bg-white"
              >
                <option value="all">Tous les statuts</option>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Demandes List */}
            <div className="space-y-3">
              {filteredDemandes.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucune demande trouvée</p>
                  </CardContent>
                </Card>
              ) : (
                filteredDemandes.map((demande) => {
                  const statusConfig = STATUS_CONFIG[demande.status] || STATUS_CONFIG.nouveau;
                  
                  return (
                    <Card 
                      key={demande.demande_id} 
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => { setSelectedDemande(demande); setShowDemandeModal(true); }}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                          {/* Client Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="font-semibold text-slate-900">{demande.name}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                                {statusConfig.label}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {demande.phone}
                              </span>
                              <span className="flex items-center gap-1">
                                <Package className="w-3 h-3" /> {demande.merchandise_type || 'Non précisé'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Addresses */}
                          <div className="flex-1 min-w-0 text-sm">
                            <div className="flex items-start gap-1 text-slate-600 mb-1">
                              <MapPin className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                              <span className="truncate">{demande.address_from}</span>
                            </div>
                            <div className="flex items-start gap-1 text-slate-600">
                              <MapPin className="w-3 h-3 mt-0.5 text-red-500 flex-shrink-0" />
                              <span className="truncate">{demande.address_to}</span>
                            </div>
                          </div>
                          
                          {/* Meta */}
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <div className="text-right">
                              <p className="font-medium">{demande.volume || '-'}</p>
                              <p>{demande.date || 'Date flexible'}</p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Vue Planning */}
        {view === 'planning' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Planning des livraisons B2C
                </CardTitle>
              </CardHeader>
              <CardContent>
                {planning.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">
                    Aucune livraison planifiée pour le moment
                  </p>
                ) : (
                  <div className="space-y-3">
                    {planning.map((item) => {
                      const driver = employees.find(e => e.user_id === item.assigned_driver);
                      return (
                        <div key={item.demande_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-semibold">{item.name}</p>
                            <p className="text-sm text-slate-500">
                              {item.address_from} → {item.address_to}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {item.merchandise_type || 'Transport'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-blue-600">
                              {item.planned_date || item.date || 'À planifier'}
                            </p>
                            <p className="text-sm text-slate-500">
                              {driver ? `${driver.first_name} ${driver.last_name}` : 'Chauffeur à assigner'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Vue Devis */}
        {view === 'devis' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Euro className="w-5 h-5 text-purple-500" />
                Gestion des devis B2C
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-slate-500 py-8">
                Les devis clients apparaîtront ici.
                <br />
                <span className="text-sm">Créez un devis depuis une demande client.</span>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Vue Missions */}
        {view === 'missions' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-indigo-500" />
                Missions / Interventions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {planning.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  Aucune mission en cours
                </p>
              ) : (
                <div className="space-y-3">
                  {planning.map((item) => (
                    <div key={item.demande_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border-l-4 border-indigo-500">
                      <div>
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-sm text-slate-500">{item.address_from} → {item.address_to}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${STATUS_CONFIG[item.status]?.color || 'bg-gray-100'}`}>
                        {STATUS_CONFIG[item.status]?.label || item.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vue Clients */}
        {view === 'clients' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-500" />
                Base clients B2C
              </CardTitle>
            </CardHeader>
            <CardContent>
              {demandes.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  Aucun client enregistré
                </p>
              ) : (
                <div className="space-y-3">
                  {/* Group demandes by client email */}
                  {[...new Map(demandes.map(d => [d.email, d])).values()].map((client) => (
                    <div key={client.email} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{client.name}</p>
                          <p className="text-sm text-slate-500">{client.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">{client.phone}</p>
                        <p className="text-xs text-slate-400">
                          {demandes.filter(d => d.email === client.email).length} demande(s)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vue Facturation */}
        {view === 'facturation' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-emerald-500" />
                Facturation B2C
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-slate-500 py-8">
                Module de facturation en cours de développement.
                <br />
                <span className="text-sm">Les factures clients apparaîtront ici.</span>
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Demande Detail Modal */}
      <Dialog open={showDemandeModal} onOpenChange={setShowDemandeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              Détail de la demande
            </DialogTitle>
          </DialogHeader>
          
          {selectedDemande && (
            <div className="space-y-6">
              {/* Status */}
              <div>
                <Label className="text-slate-700 mb-2 block">Statut</Label>
                <select
                  value={selectedDemande.status}
                  onChange={(e) => updateDemandeStatus(selectedDemande.demande_id, e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                >
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-sm">Nom</Label>
                  <p className="font-medium">{selectedDemande.name}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Téléphone</Label>
                  <a 
                    href={`https://wa.me/33${selectedDemande.phone?.replace(/^0/, '').replace(/\s/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-green-600 hover:underline flex items-center gap-1"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {selectedDemande.phone}
                  </a>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-500 text-sm">Email</Label>
                  <a href={`mailto:${selectedDemande.email}`} className="font-medium text-blue-600 hover:underline">
                    {selectedDemande.email}
                  </a>
                </div>
              </div>

              {/* Addresses */}
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-500 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-green-500" /> Adresse de départ
                  </Label>
                  <p className="font-medium">{selectedDemande.address_from}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-red-500" /> Adresse d'arrivée
                  </Label>
                  <p className="font-medium">{selectedDemande.address_to}</p>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-sm">Type de marchandise</Label>
                  <p className="font-medium">{selectedDemande.merchandise_type || 'Non précisé'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Volume</Label>
                  <p className="font-medium">{selectedDemande.volume || 'Non précisé'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Date souhaitée</Label>
                  <p className="font-medium">{selectedDemande.date || 'Flexible'}</p>
                </div>
              </div>

              {selectedDemande.message && (
                <div>
                  <Label className="text-slate-500 text-sm">Message du client</Label>
                  <p className="p-3 bg-slate-100 rounded-lg text-sm">{selectedDemande.message}</p>
                </div>
              )}

              <div className="text-xs text-slate-400">
                Demande reçue le {new Date(selectedDemande.created_at).toLocaleDateString('fr-FR')} à {new Date(selectedDemande.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button
                  onClick={() => setShowQuoteModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Créer un devis
                </Button>
                <Button
                  onClick={() => setShowPlanningModal(true)}
                  variant="outline"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Planifier
                </Button>
                <a
                  href={`https://wa.me/33${selectedDemande.phone?.replace(/^0/, '').replace(/\s/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="text-green-600 border-green-600 hover:bg-green-50">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <Button
                  onClick={() => deleteDemande(selectedDemande.demande_id)}
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Modal */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-500" />
              Créer un devis
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Prix du transport (€)</Label>
              <Input
                type="number"
                value={quoteData.transport_price}
                onChange={(e) => setQuoteData({...quoteData, transport_price: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Frais de main d'œuvre (€)</Label>
              <Input
                type="number"
                value={quoteData.labor_price}
                onChange={(e) => setQuoteData({...quoteData, labor_price: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Frais supplémentaires (€)</Label>
              <Input
                type="number"
                value={quoteData.extra_fees}
                onChange={(e) => setQuoteData({...quoteData, extra_fees: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Description des frais supplémentaires</Label>
              <Input
                value={quoteData.extra_description}
                onChange={(e) => setQuoteData({...quoteData, extra_description: e.target.value})}
                placeholder="Ex: Manutention spéciale, urgence..."
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={quoteData.notes}
                onChange={(e) => setQuoteData({...quoteData, notes: e.target.value})}
                placeholder="Notes supplémentaires..."
                rows={3}
              />
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">Total du devis</p>
              <p className="text-3xl font-bold text-blue-600">{calculateTotal()} €</p>
            </div>

            <div className="flex gap-2">
              <Button onClick={generateQuotePDF} variant="outline" className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              <Button onClick={sendQuoteByEmail} className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4 mr-2" />
                Envoyer par email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Planning Modal */}
      <Dialog open={showPlanningModal} onOpenChange={setShowPlanningModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Planifier la livraison
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date de l'intervention</Label>
              <Input
                type="date"
                value={planningData.date}
                onChange={(e) => setPlanningData({...planningData, date: e.target.value})}
              />
            </div>
            <div>
              <Label>Heure</Label>
              <Input
                type="time"
                value={planningData.time}
                onChange={(e) => setPlanningData({...planningData, time: e.target.value})}
              />
            </div>
            <div>
              <Label>Chauffeur assigné</Label>
              <select
                value={planningData.assigned_driver}
                onChange={(e) => setPlanningData({...planningData, assigned_driver: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Sélectionner un chauffeur</option>
                {employees.map((emp) => (
                  <option key={emp.user_id} value={emp.user_id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={planningData.notes}
                onChange={(e) => setPlanningData({...planningData, notes: e.target.value})}
                placeholder="Instructions pour le chauffeur..."
                rows={3}
              />
            </div>

            <Button onClick={savePlanning} className="w-full bg-blue-600 hover:bg-blue-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmer la planification
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminLivraisonB2C;
